import CommitmentFollowupBadge from './CommitmentFollowupBadge'
import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { EngagedTopic } from '../lib/api'
import { chatTarget, isCodeChatId, TODO_CHAT_ID } from '../lib/chatNavigation'
import { withChatListReturn } from '../lib/chatNavigation'
import { useChatListReturn } from '../lib/chatListReturn'
import { relativeTimeFromNow } from '../lib/time'
import AgentBadge from './AgentBadge'
import EngagedTopicDoneButton from './EngagedTopicDoneButton'
import { useUi } from '../store/ui'

const COLLAPSED_SECTIONS_KEY = 'lane-control:engaged-topics-collapsed'

function loadCollapsedSections(): string[] {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(COLLAPSED_SECTIONS_KEY) ?? '[]')
    if (Array.isArray(saved)) return saved.filter((id): id is string => typeof id === 'string')
  } catch { /* Keep sections expanded if storage is unavailable or invalid. */ }
  return []
}

export default function EngagedTopicsView({ topics, loading, error, onRetry }: {
  topics: EngagedTopic[]
  loading: boolean
  error: boolean
  onRetry: () => void
}) {
  const [collapsedSections, setCollapsedSections] = useState(loadCollapsedSections)
  const returnTo = useChatListReturn()
  const laptop = useUi((s) => s.effectiveWidth >= 1024)
  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(collapsedSections)) }
    catch { /* The toggle still works when storage is unavailable. */ }
  }, [collapsedSections])
  function toggleSection(id: string) {
    setCollapsedSections((current) => current.includes(id)
      ? current.filter((sectionId) => sectionId !== id)
      : [...current, id])
  }
  const sections = [
    { id: 'code', label: 'Code conversations', topics: topics.filter((topic) => isCodeChatId(topic.chat_id)) },
    { id: 'groups', label: 'Group topics', topics: topics.filter((topic) => !isCodeChatId(topic.chat_id) && topic.chat_id !== TODO_CHAT_ID) },
    { id: 'todoist', label: 'Todoist Tasks', topics: topics.filter((topic) => topic.chat_id === TODO_CHAT_ID) },
  ]
  return (
    <section aria-label="Topics I've replied to">
      {error && <div role="status" className="px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
        Could not refresh topics. <button type="button" onClick={onRetry} className="underline min-h-11">Retry</button>
      </div>}
      {loading && <p className="p-3 text-sm text-stone-500">Loading topics…</p>}
      {!loading && !error && topics.length === 0 && <p className="p-3 text-sm text-stone-500">No topics match this filter.</p>}
      {sections.map((section) => section.topics.length > 0 && <div key={section.id}>
        <h3>
          <button
            type="button"
            aria-expanded={!collapsedSections.includes(section.id)}
            aria-controls={`engaged-topics-${section.id}`}
            onClick={() => toggleSection(section.id)}
            className="flex min-h-11 w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-stone-500 hover:bg-stone-100 focus-visible:outline-2 focus-visible:outline-blue-500 dark:hover:bg-stone-800/60"
          >
            {collapsedSections.includes(section.id)
              ? <ChevronRight size={16} aria-hidden="true" />
              : <ChevronDown size={16} aria-hidden="true" />}
            {section.label} · {section.topics.length}
          </button>
        </h3>
        <div id={`engaged-topics-${section.id}`} hidden={collapsedSections.includes(section.id)}>
        {section.topics.map((topic) => (
        <div
          key={topic.id}
          data-engaged-topic-id={topic.id}
          className="isolate flex items-center gap-3 border-b border-stone-100 px-3 py-2.5 dark:border-stone-800/60"
        >
          <Link
            to={withChatListReturn(chatTarget(topic.chat_id, topic.id, { parentTab: 'lanes' }), returnTo)}
            aria-label={topic.title || 'Untitled topic'}
            className="shrink-0"
          >
            <AgentBadge agent={topic.agent} size={30} sessionRef={topic.session_ref} />
          </Link>
          <div className="min-w-0 flex-1">
            <Link
              to={withChatListReturn(chatTarget(topic.chat_id, topic.id, { parentTab: 'lanes' }), returnTo)}
              className="relative z-10 block rounded hover:bg-stone-100 dark:hover:bg-stone-800/60"
            >
              <div className="truncate text-sm font-semibold">{topic.title || 'Untitled topic'}</div>
              <CommitmentFollowupBadge items={topic.commitment_items} snoozedUntil={topic.snoozed_until} />
              {!laptop && topic.pending_approval && <div className="text-xs font-semibold text-amber-700 dark:text-amber-300">Approval needed</div>}
            </Link>
            {!laptop && <div className="flex min-h-5 min-w-0 items-center gap-2">
              <Link
                to={withChatListReturn(chatTarget(topic.chat_id, topic.id, { parentTab: 'lanes' }), returnTo)}
                className="flex min-w-0 flex-1 items-center gap-1 whitespace-nowrap text-xs text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800/60"
              >
                <span className="truncate">{topic.chat_display_name || topic.chat_id}</span>
                <span className="shrink-0 whitespace-nowrap">· {relativeTimeFromNow(topic.last_activity_at)}</span>
              </Link>
              <EngagedTopicDoneButton topic={topic} />
              {topic.unread > 0 && <span className="shrink-0 rounded-full bg-blue-600 px-1.5 py-0.5 text-xs text-white" aria-label={`${topic.unread} unread messages`}>{topic.unread}</span>}
            </div>}
          </div>
          {laptop && topic.unread > 0 && <span className="shrink-0 rounded-full bg-blue-600 px-1.5 py-0.5 text-xs text-white" aria-label={`${topic.unread} unread messages`}>{topic.unread}</span>}
        </div>
        ))}
        </div>
      </div>)}
    </section>
  )
}
